// Billing Settings Page
// Manage subscription, view invoices, access Stripe billing portal

import { useState } from 'react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { format } from 'date-fns';

// ============================================================
// TYPES
// ============================================================

type Product = 'fra' | 'ebm' | 'bundle';
type Tier = 'basic' | 'premium' | 'elite' | 'enterprise';
type BillingCycle = 'monthly' | 'annual';

const SELF_SERVE_PRODUCT: Product = 'fra';
const SELF_SERVE_TIERS: Tier[] = ['basic', 'premium', 'elite'];
const CONTACT_SALES_URL = 'https://calculom.com/contact';

function getTrialDaysLeft(trialEndsAt: string): number {
  const trialEnd = new Date(trialEndsAt);
  const now = new Date();
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / millisecondsPerDay);

  return Math.max(0, daysLeft);
}

// ============================================================
// PRICING DATA (matches backend)
// ============================================================

const TIER_NAMES: Record<Tier, string> = {
  basic: 'Basic',
  premium: 'Premium',
  elite: 'Elite',
  enterprise: 'Enterprise',
};

const PRICES: Record<Product, Record<Tier, { monthly: number; annual: number }>> = {
  fra: {
    basic: { monthly: 299, annual: 2988 },
    premium: { monthly: 719, annual: 7188 },
    elite: { monthly: 1199, annual: 11388 },
    enterprise: { monthly: 0, annual: 0 },
  },
  ebm: {
    basic: { monthly: 59, annual: 588 },
    premium: { monthly: 119, annual: 1188 },
    elite: { monthly: 239, annual: 2388 },
    enterprise: { monthly: 0, annual: 0 },
  },
  bundle: {
    basic: { monthly: 78, annual: 778 },
    premium: { monthly: 147, annual: 1450 },
    elite: { monthly: 286, annual: 2824 },
    enterprise: { monthly: 0, annual: 0 },
  },
};

// ============================================================
// STATUS BADGE COMPONENT
// ============================================================

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    trialing: 'bg-blue-100 text-blue-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    canceled: 'bg-red-100 text-red-800',
    paused: 'bg-gray-100 text-gray-800',
    incomplete: 'bg-orange-100 text-orange-800',
  };

  const labels: Record<string, string> = {
    active: 'Active',
    trialing: 'Trial',
    past_due: 'Past Due',
    canceled: 'Canceled',
    paused: 'Paused',
    incomplete: 'Incomplete',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {labels[status] || status}
    </span>
  );
}

// ============================================================
// USAGE METER COMPONENT
// ============================================================

function UsageMeter({ label, current, limit }: { label: string; current: number; limit: number | null }) {
  const isUnlimited = limit === null || limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((current / limit) * 100, 100);
  const isNearLimit = !isUnlimited && percentage >= 80;

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className={`font-medium ${isNearLimit ? 'text-orange-600' : 'text-gray-900'}`}>
          {current} / {isUnlimited ? '∞' : limit}
        </span>
      </div>
      {!isUnlimited && (
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isNearLimit ? 'bg-orange-500' : 'bg-primary-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// CHANGE PLAN MODAL
// ============================================================

interface ChangePlanModalProps {
  currentTier: Tier;
  currentCycle: BillingCycle;
  onClose: () => void;
  onConfirm: (tier: Tier, cycle: BillingCycle) => void;
  isPending: boolean;
  errorMessage?: string;
}

function ChangePlanModal({
  currentTier,
  currentCycle,
  onClose,
  onConfirm,
  isPending,
  errorMessage,
}: ChangePlanModalProps) {
  const [tier, setTier] = useState<Tier>(SELF_SERVE_TIERS.includes(currentTier) ? currentTier : 'premium');
  const [cycle, setCycle] = useState<BillingCycle>(currentCycle);

  const tiers = SELF_SERVE_TIERS;

  const price = PRICES[SELF_SERVE_PRODUCT][tier];
  const displayPrice = cycle === 'monthly' ? price.monthly : price.annual;

  const hasChanges = tier !== currentTier || cycle !== currentCycle;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Plan</h3>

          {/* Tier Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tier</label>
            <div className="grid grid-cols-3 gap-2">
              {tiers.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  className={`p-3 rounded-lg border-2 text-center transition-colors ${
                    tier === t ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  <span className="font-medium">{TIER_NAMES[t]}</span>
                </button>
              ))}
            </div>
            <a
              href={CONTACT_SALES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-between rounded-lg border border-dashed border-gray-300 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <div>
                <div className="font-medium text-gray-900">{TIER_NAMES.enterprise}</div>
                <p className="text-sm text-gray-600">Need enterprise rollout, onboarding, or custom deployment support?</p>
              </div>
              <span className="rounded-md border border-primary-600 px-3 py-2 text-sm font-medium text-primary-600">
                Contact Sales
              </span>
            </a>
          </div>

          {/* Billing Cycle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing Cycle</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCycle('monthly')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  cycle === 'monthly' ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setCycle('annual')}
                className={`p-3 rounded-lg border-2 text-center transition-colors ${
                  cycle === 'annual' ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                Annual (Save ~17%)
              </button>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">New Plan Price</span>
              <span className="text-2xl font-bold text-gray-900">
                ${displayPrice}
                <span className="text-sm font-normal text-gray-500">/{cycle === 'monthly' ? 'mo' : 'yr'}</span>
              </span>
            </div>
            {hasChanges && (
              <p className="text-sm text-gray-500 mt-2">
                Changes will take effect at your next billing cycle.
              </p>
            )}
          </div>

          {errorMessage && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(tier, cycle)}
              disabled={!hasChanges || isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Updating...' : 'Confirm Change'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN BILLING PAGE
// ============================================================

export function BillingPage() {
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch current tenant data (includes subscription)
  const { data: tenant, isLoading: tenantLoading, refetch: refetchTenant } = trpc.tenant.getCurrent.useQuery();

  // Fetch usage stats
  const { data: usage, isLoading: usageLoading, refetch: refetchUsage } = trpc.tenant.getUsage.useQuery();

  // Fetch invoices
  const { data: invoices, isLoading: invoicesLoading, refetch: refetchInvoices } = trpc.subscription.getInvoices.useQuery();

  // Change plan mutation
  const changePlanMutation = trpc.subscription.changePlan.useMutation({
    onSuccess: async (data) => {
      const checkoutResult = data as { action?: string; url?: string | null };

      if (checkoutResult.action === 'checkout_required' && checkoutResult.url) {
        window.location.assign(checkoutResult.url);
        return;
      }

      await refetchTenant();
      await refetchUsage();
      setShowChangePlanModal(false);
    },
  });

  const confirmCheckoutSessionMutation = trpc.subscription.confirmCheckoutSession.useMutation({
    onSuccess: async () => {
      await refetchTenant();
      await refetchUsage();
      await refetchInvoices();
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('checkout');
      nextParams.delete('session_id');
      setSearchParams(nextParams, { replace: true });
    },
  });

  const refreshFromStripeMutation = trpc.subscription.refreshFromStripe.useMutation({
    onSuccess: async () => {
      await refetchTenant();
      await refetchUsage();
      await refetchInvoices();
    },
  });

  // Cancel subscription mutation
  const cancelMutation = trpc.subscription.cancel.useMutation({
    onSuccess: () => {
      refetchTenant();
      setShowCancelConfirm(false);
    },
  });

  // Reactivate subscription mutation
  const reactivateMutation = trpc.subscription.reactivate.useMutation({
    onSuccess: () => {
      refetchTenant();
    },
  });

  // Get billing portal URL mutation
  const billingPortalMutation = trpc.subscription.getBillingPortalUrl.useMutation({
    onSuccess: (data) => {
      window.open(data.url, '_blank');
    },
  });

  const handleChangePlan = (tier: Tier, cycle: BillingCycle) => {
    changePlanMutation.mutate({ product: SELF_SERVE_PRODUCT, tier, billingCycle: cycle });
  };

  const subscription = tenant?.subscription;
  const fallbackPlanAmount = subscription
    ? PRICES[subscription.product as Product]?.[subscription.tier as Tier]?.[subscription.billingCycle as BillingCycle]
    : 0;

  useEffect(() => {
    const checkoutState = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');

    if (
      checkoutState === 'success' &&
      sessionId &&
      !confirmCheckoutSessionMutation.isPending &&
      !confirmCheckoutSessionMutation.isSuccess
    ) {
      confirmCheckoutSessionMutation.mutate({ sessionId });
    }
  }, [confirmCheckoutSessionMutation, searchParams]);

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Current Plan Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h2>
        {confirmCheckoutSessionMutation.error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {confirmCheckoutSessionMutation.error.message}
          </div>
        )}
        {refreshFromStripeMutation.error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {refreshFromStripeMutation.error.message}
          </div>
        )}
        {searchParams.get('checkout') === 'success' && confirmCheckoutSessionMutation.isPending && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
            Finalizing your plan change...
          </div>
        )}
        {subscription ? (
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xl font-semibold">{subscription.productName}</span>
                  <StatusBadge status={subscription.status} />
                </div>
                <div className="text-lg opacity-90">{subscription.tierName} Tier</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">
                  ${subscription.currentPlanAmount ?? fallbackPlanAmount}
                  <span className="text-sm font-normal opacity-80">
                    /{subscription.billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
                <div className="text-sm opacity-80 capitalize">{subscription.billingCycle} billing</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {subscription.status === 'trialing' && subscription.trialEndsAt && (
                <div>
                  <span className="opacity-80">Trial:</span>{' '}
                  <span className="font-medium">{getTrialDaysLeft(subscription.trialEndsAt)} days left</span>
                  <span className="opacity-80"> until </span>
                  <span className="font-medium">{format(new Date(subscription.trialEndsAt), 'MMM d, yyyy')}</span>
                </div>
              )}
              <div>
                <span className="opacity-80">Next billing:</span>{' '}
                <span className="font-medium">{format(new Date(subscription.currentPeriodEnd), 'MMM d, yyyy')}</span>
              </div>
              {subscription.cancelAtPeriodEnd && (
                <div className="col-span-2 bg-white/20 rounded-lg p-3 mt-2">
                  <span className="font-medium">Cancellation scheduled</span>
                  <p className="text-sm opacity-90">Your subscription will end on {format(new Date(subscription.currentPeriodEnd), 'MMM d, yyyy')}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-gray-100 rounded-xl p-6 text-center">
            <p className="text-gray-600">No active subscription</p>
            <a href="/signup" className="inline-block mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700">
              Choose a Plan
            </a>
          </div>
        )}

        {/* Plan Actions */}
        {subscription && (
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => {
                if (subscription.tier === 'enterprise') {
                  window.open(CONTACT_SALES_URL, '_blank', 'noopener,noreferrer');
                  return;
                }
                setShowChangePlanModal(true);
              }}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              {subscription.tier === 'enterprise' ? 'Contact Sales' : 'Change Plan'}
            </button>
            <button
              onClick={() => refreshFromStripeMutation.mutate()}
              disabled={refreshFromStripeMutation.isPending}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              {refreshFromStripeMutation.isPending ? 'Syncing...' : 'Sync From Stripe'}
            </button>
            <button
              onClick={() => billingPortalMutation.mutate()}
              disabled={billingPortalMutation.isPending}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              {billingPortalMutation.isPending ? 'Loading...' : 'Manage Payment Method'}
            </button>
            {subscription.cancelAtPeriodEnd ? (
              <button
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate Subscription'}
              </button>
            ) : (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="px-4 py-2 text-red-600 hover:text-red-700 font-medium"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        )}
      </section>

      {/* Usage Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage</h2>
        <div className="bg-gray-50 rounded-xl p-6">
          {usageLoading ? (
            <div className="text-center py-4 text-gray-500">Loading usage...</div>
          ) : usage ? (
            <div className="grid gap-6 md:grid-cols-2">
              <UsageMeter label="Users" current={usage.users.current} limit={usage.users.limit} />
              <UsageMeter label="ERP Connections" current={usage.connections.current} limit={usage.connections.limit} />
              <UsageMeter label="Companies" current={usage.companies.current} limit={usage.companies.limit} />
              <UsageMeter label="Templates" current={usage.templates.current} limit={usage.templates.limit} />
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">No usage data available</div>
          )}
        </div>
      </section>

      {/* Invoice History Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice History</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {invoicesLoading ? (
            <div className="p-6 text-center text-gray-500">Loading invoices...</div>
          ) : invoices && invoices.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {format(new Date(invoice.periodStart), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {invoice.invoiceNumber || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      ${(invoice.amountPaid / 100).toFixed(2)} {invoice.currency.toUpperCase()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {invoice.hostedInvoiceUrl && (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          View
                        </a>
                      )}
                      {invoice.invoicePdf && (
                        <>
                          <span className="mx-2 text-gray-300">|</span>
                          <a
                            href={invoice.invoicePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            PDF
                          </a>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No invoices yet. Your first invoice will appear after your trial ends.
            </div>
          )}
        </div>
      </section>

      {/* Change Plan Modal */}
      {showChangePlanModal && subscription && (
        <ChangePlanModal
          currentTier={subscription.tier as Tier}
          currentCycle={subscription.billingCycle as BillingCycle}
          onClose={() => {
            changePlanMutation.reset();
            setShowChangePlanModal(false);
          }}
          onConfirm={handleChangePlan}
          isPending={changePlanMutation.isPending}
          errorMessage={changePlanMutation.error?.message}
        />
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowCancelConfirm(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Subscription?</h3>
              <p className="text-gray-600 mb-4">
                Your subscription will remain active until the end of the current billing period.
                You can reactivate at any time before then.
              </p>
              {cancelMutation.error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {cancelMutation.error.message}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {cancelMutation.isPending ? 'Canceling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
